import React from 'react';
import moment from 'moment';
import CurrencyFormat from 'react-currency-format';
import cn from '@/utils/cn';
import { batchOverviewModal, scheduledPaymentModal } from '@/utils/translations';
import { DATE_FORMAT_FULL_WEEKDAY } from '@/utils/dateFormats';
import { TPaymentListItem } from '../types/ManagePayments';

type TStatusInfo = { label: string; colorClass: string };

interface BatchOverviewHeaderProps {
  titleId: string;
  payment: TPaymentListItem;
  createdByName: string;
  statusInfo: TStatusInfo;
  /** Whether this batch finished with at least one failure - gates the paid-amount suffix. */
  isIncomplete: boolean;
  isLoadingPayments: boolean;
  paidAmount: number;
  /** Joined with ", " since a multi-sign rule can require more than one approval. */
  approvedNames: string[];
  rejectedNames: string[];
  rejectionReason?: string | null;
}

/**
 * The name/total/pay-from/schedule/who-approved-or-rejected block plus the big status headline
 * at the top of Batch Overview - split out of BatchOverviewModal since it's pure presentation
 * (no action handlers, just the payment/derived-display props it's given).
 */
const BatchOverviewHeader: React.FC<BatchOverviewHeaderProps> = ({
  titleId,
  payment,
  createdByName,
  statusInfo,
  isIncomplete,
  isLoadingPayments,
  paidAmount,
  approvedNames,
  rejectedNames,
  rejectionReason
}) => (
  <div className="flex max-w-md flex-col gap-y-4 px-5 pb-6">
    <h4 id={titleId} className="text-2xl font-medium text-emerge-navy">
      {batchOverviewModal.title}
    </h4>

    <div className="text-sm font-medium leading-5 text-emerge-navy">
      <div>
        {batchOverviewModal.batchNameLabel} {payment.name}
      </div>
      <div>
        {batchOverviewModal.batchTotalLabel}{' '}
        <CurrencyFormat
          value={Number(payment.totalAmount).toFixed(2)}
          displayType="text"
          thousandSeparator
          prefix="$"
          decimalScale={2}
        />
        {isIncomplete && !isLoadingPayments && (
          <>{batchOverviewModal.paidAmountSuffix(`$${paidAmount.toFixed(2)}`)}</>
        )}
      </div>
      <div>
        {batchOverviewModal.payFromLabel}{' '}
        {payment.payFromAccount?.name ?? scheduledPaymentModal.emptyValue}
      </div>
      <div>
        {batchOverviewModal.scheduledForLabel}{' '}
        {moment(payment.scheduledDate).format(DATE_FORMAT_FULL_WEEKDAY)}
      </div>
      <div>
        {batchOverviewModal.createdByLabel} {createdByName}
      </div>
      {approvedNames.length > 0 && (
        <div>
          {batchOverviewModal.approvedByLabel} {approvedNames.join(', ')}
        </div>
      )}
      {rejectedNames.length > 0 && (
        <div className="text-error-darkred">
          {batchOverviewModal.rejectedByLabel} {rejectedNames.join(', ')}
        </div>
      )}
    </div>

    <p className={cn('text-2xl font-medium', statusInfo.colorClass)}>{statusInfo.label}</p>
    {rejectionReason && (
      <p className="text-sm font-medium text-error-darkred">
        {batchOverviewModal.reasonLabel} {rejectionReason}
      </p>
    )}
  </div>
);

export default BatchOverviewHeader;
